import { useCallback, useContext, useState } from "react";
import { ColumnVisibilityState } from "@tanstack/react-table";
import { UserContext } from "@app/user/UserContext";
import { networkPortfolioManagementColumnVisibilityStorageKey } from "@app/network-portfolio-management/utils/projectTableUtils";

/**
 * Parses a stored ColumnVisibilityState, tolerating missing/corrupt/stale data by falling back to
 * an empty object — TanStack treats a missing column id as visible, so this degrades safely to
 * "everything visible" rather than throwing or hiding columns unexpectedly.
 */
const parseStoredVisibility = (stored: string | null): ColumnVisibilityState => {
  if (!stored) {
    return {};
  }
  try {
    const parsed = JSON.parse(stored);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const useNPMColumnVisibility = (): [ColumnVisibilityState, (visibility: ColumnVisibilityState) => void] => {
  const user = useContext(UserContext);

  const [columnVisibility, setColumnVisibilityState] = useState<ColumnVisibilityState>(() => {
    if (!user) return {};
    return parseStoredVisibility(localStorage.getItem(networkPortfolioManagementColumnVisibilityStorageKey(user)));
  });

  const setColumnVisibility = useCallback((visibility: ColumnVisibilityState) => {
    if (user) {
      localStorage.setItem(networkPortfolioManagementColumnVisibilityStorageKey(user), JSON.stringify(visibility));
    }
    setColumnVisibilityState(visibility);
  }, [user]);

  return [columnVisibility, setColumnVisibility];
};

export default useNPMColumnVisibility;
